/**
 * BusinessVoiceOrderCallClient
 * ---------------------------------------------------------------------
 * Drives one end-to-end "manage my catalog by voice" call against the
 * Gemini Live API, from opening the WebSocket with a backend-minted
 * ephemeral token through to hanging up. This is the business-facing
 * counterpart to app.carata's VoiceOrderCallClient - same transport,
 * same wire protocol, same billing/heartbeat mechanics - just pointed
 * at the `ai/voice-business/sessions` endpoints and talking to a
 * business-scoped tool set (see BusinessVoiceOrderToolRegistry.php on
 * the backend) instead of the shopping/ordering one.
 *
 * This class is intentionally framework-agnostic (plain axios + native
 * WebSocket/Web Audio APIs) rather than written against this app's
 * modular-openscriptjs Mediator/event system, so it can be driven from
 * a Mediator, a view, or a quick manual test without any other app
 * wiring. A thin mediator (see src/mediators/BusinessVoiceOrderCall.js)
 * is the suggested integration point: it should instantiate this class,
 * wire its `on(...)` events to $e broadcasts, and hand it to whatever
 * view renders the call widget.
 *
 * Usage:
 *   const call = new BusinessVoiceOrderCallClient({ apiBaseUrl: routes.base });
 *   call.on('status', (e) => console.log(e.detail.status));
 *   call.on('error', (e) => inform(e.detail.message, STRINGS.ERROR));
 *   await call.start();
 *   // ... later, user taps "hang up" ...
 *   await call.stop('client_ended');
 *
 * Most tools BusinessVoiceOrderToolRegistry exposes (product CRUD-ish
 * actions, search, branches, categories, save_memory, end_call) are
 * dispatched to the backend's `/tools/invoke` endpoint. A handful are
 * answered locally instead, the same way the customer-app class does -
 * InspectDom/InspectViewport (reading the DOM) and GetCurrentLocation
 * (reading device GPS) are things the backend has no way to do, so
 * _handleToolCall() below special-cases them (this app does not yet wire
 * up PickLocation's map-picker UI, so that one stays customer-app only).
 * There is no PickLocation branch here.
 *
 * IMPORTANT - same live-API wire-schema caveats as the customer-app
 * class this was ported from (see VoiceOrderCallClient.js's docblock):
 * the exact shape of the initial `setup` message, the incoming audio
 * chunk field path, and output transcription have not been exercised
 * against a live Gemini Live API connection from this environment.
 */
import { Capacitor } from "@capacitor/core";
import { Geolocation } from "@capacitor/geolocation";
import { VoiceCall } from "@carata/capacitor-voice-call";

export class BusinessVoiceOrderCallClient extends EventTarget {
    /**
     * @param {Object} opts
     * @param {Object} [opts.requester] This app's `api` Requester (from
     *   src/declarations.js) - preferred, since on native builds it goes
     *   through Capacitor's HTTP plugin (real native network stack, no
     *   WebView CORS/cleartext issues) instead of a plain fetch(). When
     *   omitted, falls back to a plain fetch() against `apiBaseUrl` +
     *   `getAuthToken()`, which is fine for local/web testing.
     * @param {string} [opts.apiBaseUrl] Required when `requester` is omitted.
     * @param {() => string|null} [opts.getAuthToken] Returns the bearer token for our own backend when falling back to fetch(). Defaults to reading `auth_token` from localStorage, matching Requester's default.
     * @param {string|number} [opts.companyId] The company ID managing this voice session. Defaults to window.currentCompany?.id.
     */
    constructor({ requester, apiBaseUrl, getAuthToken, sessionsPath, companyId } = {}) {
        super();

        if (!requester && !apiBaseUrl) {
            throw new Error(
                "BusinessVoiceOrderCallClient requires either `requester` (this app's api object) or `apiBaseUrl`",
            );
        }

        this.requester = requester || null;
        this.apiBaseUrl = apiBaseUrl ? apiBaseUrl.replace(/\/$/, "") : "";
        this.getAuthToken =
            getAuthToken || (() => localStorage.getItem("auth_token"));
        this.sessionsPath = sessionsPath || "/ai/voice-business/sessions";
        this.companyId = companyId || null;

        // True when this call should use the native Capacitor plugin
        // (VoiceCall) for WebSocket + audio transport instead of the
        // browser WebSocket/Web Audio path below - native Android/iOS
        // builds get their own AVAudioEngine/AudioRecord pipeline plus
        // CallKit/foreground-service background survival (see
        // packages/capacitor-voice-call). Plain web/PWA always stays on
        // the browser path.
        this._native = Capacitor.isNativePlatform();
        // Handles returned by VoiceCall.addListener(...) in
        // _attachNativeListeners(), removed in _detachNativeListeners()
        // so a stopped call doesn't leak listeners into the next one.
        this._nativeListenerHandles = [];

        this.session = null; // { session_id, token, ws_url, model, heartbeat_interval_seconds, max_minutes_allowed }
        this.ws = null;
        this.status = "idle"; // idle | starting | connecting | ringing | active | ending | ended | error

        this.mediaStream = null;
        this.audioContext = null;
        this.micSourceNode = null;
        this.micProcessorNode = null;

        this.playbackContext = null;
        this.playbackQueueTime = 0;
        this.playbackAnalyser = null;
        this._activePlaybackSources = new Set();

        // Screen Wake Lock API sentinel - see _acquireScreenWakeLock()
        // below for why this stays on the plain web API for every
        // platform here, unlike the customer app's Android-specific
        // KeepAwake plugin branch.
        this._wakeLockSentinel = null;

        // Drives the "AI is speaking" pulse in the UI - sampled off the
        // playback graph (not the mic), throttled, dispatched as an
        // "audiolevel" event with a normalized 0-1 { level }.
        this._audioLevelRAF = null;
        this._audioLevelBuffer = null;
        this._lastAudioLevelEmit = 0;

        this.heartbeatTimer = null;
        this.billedSeconds = 0;
        this.creditsCharged = 0;

        // Set once the agent's own EndCall tool call has triggered a
        // graceful hangup, so a stray second tool call can't schedule it
        // twice - see _scheduleGracefulEnd().
        this._graceEndScheduled = false;

        // Set once the opening-greeting trigger has been sent, so a
        // duplicate setupComplete (shouldn't happen, but be defensive)
        // can't make the agent greet the user twice.
        this._greetingSent = false;

        this._boundVisibility = () => this._onVisibilityChange();
        this._boundPageHide = () => this.stop("client_ended");
    }

    // -----------------------------------------------------------------
    // Public API
    // -----------------------------------------------------------------

    async start() {
        this._setStatus("starting");

        // Must happen synchronously, in this same click handler, before
        // any `await` below - see _ensurePlaybackContext()'s docblock for
        // why creating it later (lazily, off the first incoming audio
        // chunk) is the classic cause of a call that connects fine but
        // plays literally no audio.
        if (!this._native) {
            this._ensurePlaybackContext();
        }

        // Keep the screen from dimming/locking for the whole call - a
        // voice call with the screen off is otherwise indistinguishable
        // from a dead one, and Android in particular will suspend the
        // WebView's mic/audio processing once the screen locks. Best
        // effort - see the method docblock for why a failure here should
        // never block/end the call itself.
        this._acquireScreenWakeLock();

        // Split into distinct failure points with distinct messages -
        // see VoiceOrderCallClient.js's matching comment for the full
        // reasoning (microphone access is requested FIRST, before the
        // ephemeral token is minted, because the token's ~60 second
        // newSessionExpireTime window can otherwise be burned by a
        // slow first-time RECORD_AUDIO permission prompt).
        try {
            if (this._native) {
                await this._requestNativeMicPermission();
            } else {
                await this._openMicrophone();
            }
        } catch (e) {
            this._error(
                "Could not access the microphone. Check that this app has microphone permission (Android/iOS Settings) and try again.",
                e,
            );
            this._releaseScreenWakeLock();
            return;
        }

        try {
            const companyId = this.companyId || window.currentCompany?.id;
            this.session = await this._post(
                this.sessionsPath,
                companyId ? { company_id: companyId } : undefined,
            );
        } catch (e) {
            this._error(
                "Could not reach the server to start the call. Check your connection and try again.",
                e,
            );
            if (!this._native) this._stopMicrophone();
            this._releaseScreenWakeLock();
            return;
        }

        try {
            if (this._native) {
                await this._connectNative();
            } else {
                this._connectSocket();
            }
            this._attachLifecycleHandlers();
            this._startHeartbeat();
        } catch (e) {
            this._error("Could not start the voice call.", e);
            await this.stop("error");
        }
    }

    /**
     * @param {string} reason client_ended|error
     */
    async stop(reason = "client_ended") {
        if (this.status === "ended" || this.status === "idle") return;

        this._setStatus("ending");
        this._detachLifecycleHandlers();
        this._stopHeartbeat();

        if (this._native) {
            await this._disconnectNative();
        } else {
            try {
                if (this.ws && this.ws.readyState === WebSocket.OPEN) {
                    this.ws.send(
                        JSON.stringify({ realtimeInput: { audioStreamEnd: true } }),
                    );
                    this.ws.close();
                }
            } catch (e) {
                /* best effort */
            }

            this._clearPlaybackQueue();
            this._stopMicrophone();
            this._stopAudioLevelLoop();
        }

        this._releaseScreenWakeLock();

        if (this.session?.session_id) {
            try {
                await this._post(
                    `${this.sessionsPath}/${this.session.session_id}/end`,
                );
            } catch (e) {
                /* best effort - the server-side sweep is the real safety net */
            }
        }

        this._setStatus("ended");
        this.dispatchEvent(
            new CustomEvent("ended", {
                detail: {
                    reason,
                    billedSeconds: this.billedSeconds,
                    creditsCharged: this.creditsCharged,
                },
            }),
        );
    }

    // -----------------------------------------------------------------
    // Native transport (Capacitor VoiceCall plugin)
    // -----------------------------------------------------------------
    //
    // On native Android/iOS builds, the VoiceCall plugin owns the
    // WebSocket connection and the whole audio capture/playback
    // pipeline itself (see packages/capacitor-voice-call) - it exists
    // specifically so a call survives the app being backgrounded, which
    // the browser WebSocket/Web Audio path below cannot do (a
    // backgrounded WebView throttles/suspends both). This class stays
    // the single source of truth for call state either way:
    // _attachNativeListeners() below re-dispatches every native event
    // onto the exact same public events (status/transcript/audiolevel/
    // error) and internal handlers (_setStatus/_handleToolCall/_error)
    // the web path already uses, so nothing downstream - the mediator,
    // the call widget - needs to know which transport is active.

    /**
     * Primes RECORD_AUDIO permission before minting the ephemeral
     * token, mirroring _openMicrophone()'s role in start() (see that
     * method's docblock for why permission must be resolved before the
     * token's ~60s newSessionExpireTime clock starts). The plugin owns
     * mic capture itself on native - this just makes sure any
     * permission prompt has already been answered before
     * _connectNative() asks it to open the socket.
     */
    async _requestNativeMicPermission() {
        const status = await VoiceCall.checkPermissions();
        if (status.record === "granted") return;

        const result = await VoiceCall.requestPermissions();
        if (result.record !== "granted") {
            throw new Error("Microphone permission was not granted.");
        }
    }

    /**
     * Native-transport counterpart to _connectSocket() - hands the
     * minted session straight to the VoiceCall plugin, which owns the
     * connection and audio pipeline from here on.
     */
    async _connectNative() {
        this._attachNativeListeners();
        this._setStatus("connecting");
        await VoiceCall.startCall({
            wsUrl: this.session.ws_url,
            accessToken: this.session.token,
            model: this.session.model?.startsWith("models/")
                ? this.session.model
                : `models/${this.session.model}`,
        });
    }

    async _disconnectNative() {
        this._detachNativeListeners();
        try {
            await VoiceCall.endCall();
        } catch (e) {
            /* best effort */
        }
    }

    _attachNativeListeners() {
        this._nativeListenerHandles.push(
            VoiceCall.addListener("status", ({ status }) => {
                // "active" (and the visible call timer it starts - see
                // BusinessVoiceOrderCallModal.js's startTicker()) is set
                // natively the moment real audio playback begins,
                // mirroring _playAudioChunk()'s role on the web path.
                this._setStatus(status);
            }),
        );

        this._nativeListenerHandles.push(
            VoiceCall.addListener("toolCall", (detail) =>
                this._handleToolCall({ functionCalls: detail.functionCalls }),
            ),
        );

        this._nativeListenerHandles.push(
            VoiceCall.addListener("transcript", ({ text }) => {
                this.dispatchEvent(
                    new CustomEvent("transcript", { detail: { text } }),
                );
            }),
        );

        this._nativeListenerHandles.push(
            VoiceCall.addListener("audioLevel", ({ level }) => {
                this.dispatchEvent(
                    new CustomEvent("audiolevel", { detail: { level } }),
                );
            }),
        );

        this._nativeListenerHandles.push(
            VoiceCall.addListener("error", ({ message }) => {
                this._error(message);
            }),
        );

        this._nativeListenerHandles.push(
            VoiceCall.addListener("disconnected", ({ code, reason }) => {
                // Mirrors _connectSocket()'s ws.onclose unexpected-close
                // handling below - same guard, same user-facing
                // message, same "don't attempt to reconnect" reasoning.
                if (
                    ["connecting", "ringing", "active"].includes(this.status)
                ) {
                    console.warn(
                        `[BusinessVoiceOrderCallClient] Native call disconnected unexpectedly (code=${code}, reason="${reason || "none"}").`,
                    );
                    this._error(
                        "The call disconnected unexpectedly. Please try again in a moment.",
                    );
                    this.stop("error");
                }
            }),
        );

        this._nativeListenerHandles.push(
            VoiceCall.addListener("speakerphoneChanged", ({ enabled }) => {
                this.dispatchEvent(
                    new CustomEvent("speakerphonechanged", {
                        detail: { enabled },
                    }),
                );
            }),
        );
    }

    _detachNativeListeners() {
        this._nativeListenerHandles.forEach((handle) => {
            try {
                handle.remove();
            } catch (e) {
                /* best effort */
            }
        });
        this._nativeListenerHandles = [];
    }

    /**
     * Whether there's still agent speech queued for playback - native
     * counterpart to the web path's `ctx.currentTime < this.playbackQueueTime`
     * check, used by _scheduleGracefulEnd() below.
     */
    async _isPlaybackActive() {
        if (this._native) {
            try {
                const { active } = await VoiceCall.isPlaybackActive();
                return !!active;
            } catch (e) {
                return false;
            }
        }

        const ctx = this.playbackContext;
        return !!(ctx && ctx.currentTime < this.playbackQueueTime);
    }

    /**
     * Flips between the earpiece and the main/loudspeaker - native-only
     * (see VoiceCall.setSpeakerphoneOn's docblock in definitions.js);
     * the web transport has no equivalent JS API to switch this, so
     * this is a no-op there. Fire-and-forget - the UI keeps its own
     * optimistic toggle state (see BusinessVoiceOrderCallModal.js's
     * speakerOn) rather than waiting on a round trip for every tap.
     */
    async setSpeakerphoneOn(enabled) {
        if (!this._native) return;
        try {
            await VoiceCall.setSpeakerphoneOn({ enabled });
        } catch (e) {
            /* best effort */
        }
    }

    // -----------------------------------------------------------------
    // WebSocket
    // -----------------------------------------------------------------

    _connectSocket() {
        this._setStatus("connecting");

        const url = `${this.session.ws_url}?access_token=${encodeURIComponent(this.session.token)}`;
        this.ws = new WebSocket(url);

        this.ws.onopen = () => {
            // Minimal setup message - model/systemInstruction/tools/
            // responseModalities/sessionResumption are already locked
            // server-side via the ephemeral token's liveConnectConstraints.
            this.ws.send(
                JSON.stringify({
                    setup: {
                        model: this.session.model?.startsWith("models/")
                            ? this.session.model
                            : `models/${this.session.model}`,
                    },
                }),
            );
            // Not "active" yet - the socket being open just means Google
            // has accepted the connection and is (invisibly) working
            // through system-instruction/tool-list processing and
            // generating the opening greeting. "active" (which starts
            // the visible call timer - see BusinessVoiceOrderCallModal.js's
            // startTicker()) is set the moment the first real audio chunk
            // actually arrives, in _playAudioChunk() below.
            this._setStatus("ringing");
        };

        this.ws.onmessage = (event) => this._onSocketMessage(event);

        this.ws.onerror = (event) => {
            this._error("Voice call connection error.", event);
        };

        this.ws.onclose = (event) => {
            if (["connecting", "ringing", "active"].includes(this.status)) {
                // Log the actual close code/reason so a dropped connection
                // is diagnosable from the console alone (the Live API often
                // closes with no application-level error message). See
                // VoiceOrderCallClient.js's matching comment for the fuller
                // history of the close-code investigation this mirrors.
                console.warn(
                    `[BusinessVoiceOrderCallClient] WebSocket closed unexpectedly (code=${event.code}, reason="${event.reason || "none"}", wasClean=${event.wasClean}).`,
                );
                this._error(
                    "The call disconnected unexpectedly. Please try again in a moment.",
                    event,
                );
                // Server closed the socket without us asking - finalize on
                // our side too so the UI/heartbeat stop, even though the
                // backend sweep will independently true-up billing.
                this.stop("error");
            }
        };
    }

    /**
     * Kick off the agent's first turn right after setup is acknowledged,
     * so it opens the call with its greeting instead of sitting silent
     * until the caller speaks. This is a synthetic client turn, not real
     * user speech - the system prompt tells the agent how to interpret
     * and respond to it.
     */
    _sendGreetingTrigger() {
        if (this._greetingSent) return;
        this._greetingSent = true;

        if (this.ws?.readyState !== WebSocket.OPEN) return;

        this.ws.send(
            JSON.stringify({
                clientContent: {
                    turns: [
                        {
                            role: "user",
                            parts: [
                                {
                                    text: "(The call has just connected. Greet the user now, exactly as instructed in your system prompt.)",
                                },
                            ],
                        },
                    ],
                    turnComplete: true,
                },
            }),
        );
    }

    async _onSocketMessage(event) {
        let raw = event.data;
        // The Live API can send Blob frames for binary audio-bearing
        // messages depending on transport - normalize to text/JSON.
        if (raw instanceof Blob) {
            raw = await raw.text();
        }

        let message;
        try {
            message = JSON.parse(raw);
        } catch (e) {
            return;
        }

        if (message.setupComplete) {
            // The Live API never speaks first on its own - it only ever
            // reacts to user input. To get the agent's opening greeting
            // (see the system prompt's "Opening" instructions), the
            // client has to kick off the first turn itself once setup is
            // acknowledged, with a synthetic cue rather than real user
            // speech.
            this._sendGreetingTrigger();
        }

        if (message.toolCall) {
            await this._handleToolCall(message.toolCall);
        }

        if (message.serverContent?.interrupted) {
            this._clearPlaybackQueue();
        }

        if (message.serverContent?.modelTurn?.parts) {
            for (const part of message.serverContent.modelTurn.parts) {
                if (part.inlineData?.data) {
                    this._playAudioChunk(part.inlineData.data);
                }
                if (part.text) {
                    this.dispatchEvent(
                        new CustomEvent("transcript", {
                            detail: { text: part.text },
                        }),
                    );
                }
            }
        }

        if (message.goAway) {
            this.dispatchEvent(
                new CustomEvent("goaway", { detail: message.goAway }),
            );
            // Phase 2: reconnect using the last sessionResumptionUpdate
            // handle instead of just ending. For now, end gracefully.
            this.stop("error");
        }

        if (message.sessionResumptionUpdate?.newHandle) {
            this._resumptionHandle = message.sessionResumptionUpdate.newHandle;
        }
    }

    /**
     * Handles a Live API tool call. Unlike the customer-app
    static FRONTEND_ONLY_TOOLS = new Set([
        "NavigateToDashboard",
        "NavigateToOrders",
        "NavigateToInventory",
        "NavigateToAnalytics",
        "NavigateToDeals",
        "NavigateToServices",
        "NavigateToBranches",
        "NavigateToStaff",
        "NavigateToProfile",
        "OpenAddProductForm",
        "InspectDom",
        "InspectViewport",
    ]);

    /**
     * Dispatches tool calls incoming from Gemini Live to either:
     * - A frontend-only handler (navigation, UI controls, DOM inspection)
     * - The backend `/tools/invoke` endpoint for server-side mutations/queries
     *
     * Responses are packed and sent back over WebSocket or native Capacitor
     * VoiceCall plugin. Dispatches a "toolcall" CustomEvent carrying { name, args, result }.
     */
    async _handleToolCall(toolCall) {
        const functionCalls = toolCall.functionCalls || [];
        const responses = [];
        let agentRequestedEnd = false;

        for (const call of functionCalls) {
            let result;
            try {
                if (call.name === "GetCurrentLocation") {
                    result = await this._getDeviceLocation();
                } else if (call.name === "InspectDom") {
                    result = this._inspectDom();
                } else if (call.name === "InspectViewport") {
                    result = this._inspectViewport();
                } else if (call.name === "ConvertTime") {
                    result = this._convertTime(call.args);
                } else {
                    const res = await this._post(
                        `${this.sessionsPath}/${this.session.session_id}/tools/invoke`,
                        {
                            tool: call.name,
                            call_id: call.id,
                            arguments: call.args || {},
                        },
                    );
                    result = res.result;
                }
            } catch (e) {
                console.error("[BusinessVoiceOrderCallClient] Tool invocation failed:", call.name, e);
                result = "This action failed on the server.";
            }

            responses.push({
                id: call.id,
                name: call.name,
                response: { result },
            });

            if (call.name === "EndCall") agentRequestedEnd = true;

            this.dispatchEvent(
                new CustomEvent("toolcall", {
                    detail: { name: call.name, args: call.args, result },
                }),
            );
        }

        if (responses.length) {
            if (this._native) {
                try {
                    await VoiceCall.sendToolResponses({ responses });
                } catch (e) {
                    /* best effort - a dropped response surfaces as a
                       stalled turn, which the disconnected/error path
                       above will already be handling if the socket is
                       actually gone */
                }
            } else if (this.ws?.readyState === WebSocket.OPEN) {
                this.ws.send(
                    JSON.stringify({
                        toolResponse: { functionResponses: responses },
                    }),
                );
            }
        }

        // The agent calls EndCall right after saying goodbye - don't cut
        // the socket immediately, or the caller hears the tail of the
        // sentence get chopped off. Wait for whatever is still queued in
        // the playback buffer to actually finish before hanging up.
        if (agentRequestedEnd) this._scheduleGracefulEnd();
    }

    /**
     * Hang up shortly after the agent's own EndCall tool call, once its
     * queued speech has finished playing (plus a small trailing buffer in
     * case one more audio chunk is still in flight). Bounded by a hard
     * cap so a stuck/never-draining queue can't leave the call open
     * forever.
     */
    _scheduleGracefulEnd() {
        if (this._graceEndScheduled) return;
        this._graceEndScheduled = true;

        const deadline = Date.now() + 8000;
        const POLL_MS = 200;
        const TRAILING_BUFFER_MS = 500;

        const check = async () => {
            if (this.status === "ended" || this.status === "idle") return;

            const stillPlaying = await this._isPlaybackActive();

            if (stillPlaying && Date.now() < deadline) {
                setTimeout(check, POLL_MS);
                return;
            }

            setTimeout(() => this.stop("client_ended"), TRAILING_BUFFER_MS);
        };

        check();
    }
    /**
     * Answers the GetCurrentLocation tool call locally using the
     * device's own geolocation, instead of relaying it to the backend -
     * the server has no way to read a device's GPS (see
     * GetCurrentLocation.php's docblock). Mirrors the customer-app
     * VoiceOrderCallClient's _getDeviceLocation() (same native/web
     * branching, same silent no-modal behavior since this runs mid
     * voice-call).
     *
     * Returns a JSON string in the same {success, message, data, meta}
     * envelope shape as App\Ai\Tools\Response::jsonSerialize(), so the
     * agent reads locally-answered and server-answered tool results
     * the same way.
     */
    async _getDeviceLocation() {
        const envelope = (success, message, data = {}) =>
            JSON.stringify({ success, message, data, meta: {} });

        try {
            if (!Capacitor.isNativePlatform()) {
                if (!navigator.geolocation) {
                    return envelope(
                        false,
                        "Geolocation is not supported on this device.",
                    );
                }

                const position = await new Promise((resolve, reject) => {
                    navigator.geolocation.getCurrentPosition(resolve, reject, {
                        enableHighAccuracy: true,
                        timeout: 20000,
                        maximumAge: 10000,
                    });
                });

                return envelope(true, "Got the current location.", {
                    latitude: position.coords.latitude,
                    longitude: position.coords.longitude,
                });
            }

            const permStatus = await Geolocation.checkPermissions();
            let granted = permStatus.location === "granted";

            if (!granted && permStatus.location === "prompt") {
                const requestResult = await Geolocation.requestPermissions();
                granted = requestResult.location === "granted";
            }

            if (!granted) {
                return envelope(
                    false,
                    "Location access is not granted on this device. Ask the user to enable location permission for Carata in their device settings.",
                );
            }

            const position = await Geolocation.getCurrentPosition({
                enableHighAccuracy: true,
                timeout: 20000,
                maximumAge: 10000,
            });

            return envelope(true, "Got the current location.", {
                latitude: position.coords.latitude,
                longitude: position.coords.longitude,
            });
        } catch (e) {
            return envelope(
                false,
                "Could not get the current location. The user may have denied location access.",
            );
        }
    }

    /**
     * Answers the InspectDom tool call locally - a lightweight text
     * summary of what's on screen (page title, route, a handful of
     * visible headings), not a picture.
     */
    _inspectDom() {
        const envelope = (success, message, data = {}) =>
            JSON.stringify({ success, message, data, meta: {} });

        try {
            const headings = Array.from(document.querySelectorAll("h1, h2, h3"))
                .map((el) => el.textContent?.trim())
                .filter(Boolean)
                .slice(0, 10);

            return envelope(true, "Got the current screen.", {
                page_title: document.title || null,
                path: window.location?.pathname || null,
                headings,
            });
        } catch (e) {
            return envelope(false, "Could not inspect the screen.");
        }
    }

    /**
     * Answers the InspectViewport tool call locally - screen size and
     * scroll position.
     */
    _inspectViewport() {
        const envelope = (success, message, data = {}) =>
            JSON.stringify({ success, message, data, meta: {} });

        try {
            return envelope(true, "Got viewport details.", {
                width: window.innerWidth,
                height: window.innerHeight,
                scrollX: window.scrollX,
                scrollY: window.scrollY,
            });
        } catch (e) {
            return envelope(false, "Could not inspect viewport.");
        }
    }

    /**
     * Answers the ConvertTime tool call locally using the user's device clock and timezone.
     * If a time string is supplied, converts it to the target timezone (defaults to device timezone).
     * Otherwise returns the current device time, date, day of week, and timezone.
     *
     * @param {Object} args
     * @param {string} [args.time] - Optional ISO or time string to convert.
     * @param {string} [args.target_timezone] - Optional target IANA timezone (defaults to user device timezone).
     * @returns {string} JSON string envelope { success, message, data, meta }
     */
    _convertTime(args = {}) {
        const envelope = (success, message, data = {}) =>
            JSON.stringify({ success, message, data, meta: {} });

        try {
            const deviceTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
            const targetTz = args?.target_timezone || deviceTimezone;

            const now = new Date();
            let dateToFormat = now;

            if (args?.time) {
                const parsed = new Date(args.time);
                if (!isNaN(parsed.getTime())) {
                    dateToFormat = parsed;
                }
            }

            const timeFormatter12 = new Intl.DateTimeFormat("en-US", {
                timeZone: targetTz,
                hour: "numeric",
                minute: "2-digit",
                second: "2-digit",
                hour12: true,
            });

            const timeFormatter24 = new Intl.DateTimeFormat("en-US", {
                timeZone: targetTz,
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
                hour12: false,
            });

            const dateFormatter = new Intl.DateTimeFormat("en-US", {
                timeZone: targetTz,
                weekday: "long",
                year: "numeric",
                month: "long",
                day: "numeric",
            });

            const shortDateFormatter = new Intl.DateTimeFormat("en-CA", {
                timeZone: targetTz,
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
            });

            const formattedTime12 = timeFormatter12.format(dateToFormat);
            const formattedTime24 = timeFormatter24.format(dateToFormat);
            const formattedDate = dateFormatter.format(dateToFormat);
            const yyyyMmDd = shortDateFormatter.format(dateToFormat);

            const data = {
                timezone: targetTz,
                device_timezone: deviceTimezone,
                current_device_time: timeFormatter12.format(now),
                current_device_time_24h: timeFormatter24.format(now),
                current_device_date: dateFormatter.format(now),
                current_device_timestamp: Math.floor(now.getTime() / 1000),
                formatted_time: formattedTime12,
                formatted_time_24h: formattedTime24,
                formatted_date: formattedDate,
                date: yyyyMmDd,
                is_converted: Boolean(args?.time),
                original_time: args?.time || null,
            };

            return envelope(
                true,
                `User's local time is ${formattedTime12} (${targetTz}) on ${formattedDate}.`,
                data
            );
        } catch (e) {
            console.error("[BusinessVoiceOrderCallClient] ConvertTime failed:", e);
            return envelope(false, "Could not convert time on device.");
        }
    }

    // -----------------------------------------------------------------
    // Microphone capture -> realtimeInput
    // -----------------------------------------------------------------

    async _openMicrophone() {
        this.mediaStream = await navigator.mediaDevices.getUserMedia({
            audio: {
                channelCount: 1,
                // sampleRate: 16000,
                echoCancellation: true,
				noiseSuppression: true,
				autoGainControl: true,
            },
        });

        this.audioContext = new (
            window.AudioContext || window.webkitAudioContext
        )({ sampleRate: 16000 });
        this.micSourceNode = this.audioContext.createMediaStreamSource(
            this.mediaStream,
        );

        // ScriptProcessorNode is deprecated but has the broadest WebView
        // compatibility for this Capacitor app; swap for an AudioWorklet
        // if/when the minimum supported WebView version allows it.
        const bufferSize = 4096;
        this.micProcessorNode = this.audioContext.createScriptProcessor(
            bufferSize,
            1,
            1,
        );

        this.micProcessorNode.onaudioprocess = (event) => {
            if (
                !["ringing", "active"].includes(this.status) ||
                !this.ws ||
                this.ws.readyState !== WebSocket.OPEN
            ) {
                return;
            }

            const input = event.inputBuffer.getChannelData(0);
            const pcm16 = BusinessVoiceOrderCallClient._floatTo16BitPCM(input);
            const base64 = BusinessVoiceOrderCallClient._arrayBufferToBase64(
                pcm16.buffer,
            );

            this.ws.send(
                JSON.stringify({
                    realtimeInput: {
                        audio: {
                            mimeType: "audio/pcm;rate=16000",
                            data: base64,
                        },
                    },
                }),
            );
        };

        this.micSourceNode.connect(this.micProcessorNode);
        // Some browsers require the processor node connected to a
        // destination to actually fire onaudioprocess.
        this.micProcessorNode.connect(this.audioContext.destination);
    }

    _stopMicrophone() {
        try {
            this.micProcessorNode?.disconnect();
            this.micSourceNode?.disconnect();
            this.mediaStream?.getTracks().forEach((t) => t.stop());
            this.audioContext?.close();
        } catch (e) {
            /* best effort */
        }
    }

    // -----------------------------------------------------------------
    // Audio playback (24kHz PCM chunks from the model)
    // -----------------------------------------------------------------

    /**
     * Create (if needed) the 24kHz playback AudioContext/analyser used
     * for every chunk of the agent's speech, and make sure it's actually
     * running. See VoiceOrderCallClient.js's matching method docblock
     * for the full explanation of why this must run synchronously
     * inside the click handler that starts the call (autoplay policy).
     */
    _ensurePlaybackContext() {
        if (!this.playbackContext) {
            this.playbackContext = new (
                window.AudioContext || window.webkitAudioContext
            )({ sampleRate: 24000 });
            this.playbackQueueTime = this.playbackContext.currentTime;

            // Every chunk routes through this one analyser on its way to
            // the speakers, so we can read back how loud the agent's
            // voice actually is right now (not just "a chunk arrived").
            this.playbackAnalyser = this.playbackContext.createAnalyser();
            this.playbackAnalyser.fftSize = 256;
            this.playbackAnalyser.smoothingTimeConstant = 0.6;
            this.playbackAnalyser.connect(this.playbackContext.destination);

            this._ensureAudioLevelLoop();
        }

        if (this.playbackContext.state !== "running") {
            this.playbackContext.resume().catch(() => {
                /* best effort - _playAudioChunk() below retries this on
                 * every chunk, so a transient failure here isn't fatal */
            });
        }

        return this.playbackContext;
    }

    _playAudioChunk(base64Data) {
        this._ensurePlaybackContext();

        // The first real audio chunk from the model is what "the call is
        // actually happening now" means to the person on the other end -
        // flip to "active" here rather than back at socket-open, so the
        // visible call timer/"Call Ongoing" label (see
        // BusinessVoiceOrderCallModal.js) start when they actually hear
        // something instead of during the several-second silent gap
        // while Google processes the system instruction/tool list and
        // generates the opening greeting.
        if (this.status === "ringing") {
            this._setStatus("active");
        }

        const bytes = BusinessVoiceOrderCallClient._base64ToArrayBuffer(base64Data);
        const pcm16 = new Int16Array(bytes);
        const float32 = new Float32Array(pcm16.length);
        for (let i = 0; i < pcm16.length; i++) {
            float32[i] = pcm16[i] / 32768;
        }

        const buffer = this.playbackContext.createBuffer(
            1,
            float32.length,
            24000,
        );
        buffer.copyToChannel(float32, 0);

        const source = this.playbackContext.createBufferSource();
        source.buffer = buffer;
        source.connect(this.playbackAnalyser);

        this._activePlaybackSources.add(source);
        source.onended = () => {
            this._activePlaybackSources.delete(source);
        };

        const startAt = Math.max(
            this.playbackQueueTime,
            this.playbackContext.currentTime,
        );
        source.start(startAt);
        this.playbackQueueTime = startAt + buffer.duration;
    }

    /**
     * Flushes and terminates all pending/scheduled audio sources when an
     * interruption occurs (barge-in: the user started speaking while the
     * model was talking), preventing the agent from speaking over the user.
     */
    _clearPlaybackQueue() {
        if (this._activePlaybackSources) {
            for (const source of this._activePlaybackSources) {
                try {
                    source.stop();
                    source.disconnect();
                } catch (e) {
                    /* already stopped */
                }
            }
            this._activePlaybackSources.clear();
        }
        if (this.playbackContext) {
            this.playbackQueueTime = this.playbackContext.currentTime;
        }
        this.dispatchEvent(
            new CustomEvent("audiolevel", { detail: { level: 0 } }),
        );
    }

    /**
     * Samples the playback analyser on every animation frame and, at
     * most every ~80ms, dispatches an "audiolevel" event with a
     * normalized { level } (0 = silent, 1 = loud) reflecting how loud
     * the agent's voice is *right now*. Runs continuously once the
     * first audio chunk arrives - reads back near 0 whenever the agent
     * isn't actively speaking, which is the correct/desired resting
     * state for a "listening" pulse.
     */
    _ensureAudioLevelLoop() {
        if (this._audioLevelRAF) return;

        const tick = () => {
            if (!this.playbackAnalyser) {
                this._audioLevelRAF = requestAnimationFrame(tick);
                return;
            }

            if (
                !this._audioLevelBuffer ||
                this._audioLevelBuffer.length !== this.playbackAnalyser.fftSize
            ) {
                this._audioLevelBuffer = new Uint8Array(
                    this.playbackAnalyser.fftSize,
                );
            }

            this.playbackAnalyser.getByteTimeDomainData(this._audioLevelBuffer);

            let sumSquares = 0;
            for (let i = 0; i < this._audioLevelBuffer.length; i++) {
                const centered = (this._audioLevelBuffer[i] - 128) / 128;
                sumSquares += centered * centered;
            }
            const rms = Math.sqrt(sumSquares / this._audioLevelBuffer.length);
            // Typical speech RMS on this scale is small - boost it into a
            // usable 0-1 range for the UI.
            const level = Math.min(1, rms * 4);

            const now =
                typeof performance !== "undefined"
                    ? performance.now()
                    : Date.now();
            if (now - this._lastAudioLevelEmit > 80) {
                this._lastAudioLevelEmit = now;
                this.dispatchEvent(
                    new CustomEvent("audiolevel", { detail: { level } }),
                );
            }

            this._audioLevelRAF = requestAnimationFrame(tick);
        };

        this._audioLevelRAF = requestAnimationFrame(tick);
    }

    _stopAudioLevelLoop() {
        if (this._audioLevelRAF) {
            cancelAnimationFrame(this._audioLevelRAF);
            this._audioLevelRAF = null;
        }
        this._lastAudioLevelEmit = 0;
        this.dispatchEvent(
            new CustomEvent("audiolevel", { detail: { level: 0 } }),
        );
    }

    // -----------------------------------------------------------------
    // Heartbeat / billing
    // -----------------------------------------------------------------

    _startHeartbeat() {
        const intervalMs =
            (this.session.heartbeat_interval_seconds || 15) * 1000;
        this.heartbeatTimer = setInterval(() => this._heartbeat(), intervalMs);
    }

    _stopHeartbeat() {
        if (this.heartbeatTimer) {
            clearInterval(this.heartbeatTimer);
            this.heartbeatTimer = null;
        }
    }

    async _heartbeat() {
        if (!this.session?.session_id || !["ringing", "active"].includes(this.status)) return;

        try {
            const res = await this._post(
                `${this.sessionsPath}/${this.session.session_id}/heartbeat`,
            );

            this.billedSeconds = res.billed_seconds;
            this.creditsCharged = res.credits_charged;

            this.dispatchEvent(
                new CustomEvent("heartbeat", {
                    detail: {
                        billedSeconds: this.billedSeconds,
                        creditsCharged: this.creditsCharged,
                    },
                }),
            );

            if (res.should_end) {
                await this.stop("client_ended");
            }
        } catch (e) {
            // A missed heartbeat is not fatal - the next one will catch up,
            // and the backend sweep is the actual correctness guarantee.
        }
    }

    // -----------------------------------------------------------------
    // App/tab lifecycle - best-effort "end on close", not the safety net
    // -----------------------------------------------------------------

    _attachLifecycleHandlers() {
        document.addEventListener("visibilitychange", this._boundVisibility);
        window.addEventListener("pagehide", this._boundPageHide);
    }

    _detachLifecycleHandlers() {
        document.removeEventListener("visibilitychange", this._boundVisibility);
        window.removeEventListener("pagehide", this._boundPageHide);
    }

    // -----------------------------------------------------------------
    // Screen wake lock
    // -----------------------------------------------------------------

    /**
     * Keep the screen on for the duration of the call, via the standard
     * Screen Wake Lock API (`navigator.wakeLock`), now supported in all
     * major browsers/WebViews this app targets.
     *
     * Deliberately simpler than VoiceOrderCallClient.js's matching
     * method: that class special-cases Android via
     * @capacitor-community/keep-awake (which sets
     * Window.FLAG_KEEP_SCREEN_ON on the native Activity directly,
     * working around inconsistent WebView Wake Lock API support across
     * OEM WebView versions) - business.carata does not currently bundle
     * that plugin (see package.json/CapApp-SPM/Package.swift), so this
     * stays on the plain web API for every platform, native included.
     * If Android screen-dimming during a call turns out to be a real
     * problem in practice, porting that plugin the same way
     * capacitor-voice-call was ported (Task #72) is the fix.
     *
     * Deliberately best-effort either way: an unsupported browser, a
     * denied/unavailable wake lock (e.g. low battery on some browsers),
     * or the page not being visible at request time should never block
     * or end the call - the person can still talk, they'll just need to
     * keep tapping their screen awake themselves.
     */
    async _acquireScreenWakeLock() {
        try {
            if (!("wakeLock" in navigator)) return;

            // Releasing and re-requesting (e.g. after a tab was briefly
            // hidden - see _onVisibilityChange() below) is fine even if a
            // sentinel is already held; avoid leaking the old one first.
            this._wakeLockSentinel?.release()?.catch(() => {});
            this._wakeLockSentinel = await navigator.wakeLock.request("screen");
        } catch (e) {
            // Most commonly: the page wasn't visible at request time (the
            // spec requires visibility), or the platform declined it.
            // Nothing actionable to show the user for this - see docblock.
        }
    }

    async _releaseScreenWakeLock() {
        try {
            await this._wakeLockSentinel?.release();
        } catch (e) {
            /* best effort */
        } finally {
            this._wakeLockSentinel = null;
        }
    }

    /**
     * Reacquires the screen wake lock when the tab comes back into view
     * after being hidden - the web Wake Lock API auto-releases itself
     * whenever a tab goes hidden (spec behavior) and does NOT re-acquire
     * on its own once it's visible again. See VoiceOrderCallClient.js's
     * matching method docblock for the fuller explanation, including why
     * a hidden tab no longer ends the call at all (the server-side sweep
     * - SweepStaleAiVoiceCallSessions - is the real protection against a
     * genuinely abandoned session).
     */
    _onVisibilityChange() {
        if (document.visibilityState === "visible") {
            if (["ringing", "active"].includes(this.status)) {
                this._acquireScreenWakeLock();
            }
        }
    }

    // -----------------------------------------------------------------
    // HTTP + helpers
    // -----------------------------------------------------------------

    async _post(path, body) {
        if (this.requester) {
            const response = await this.requester.post(path, body);
            const data =
                typeof response?.json === "function"
                    ? await response.json()
                    : response;

            if (response?.ok === false) {
                throw new Error(data?.message || `Request to ${path} failed`);
            }

            return data?.data ?? data;
        }

        const token = this.getAuthToken();

        const response = await fetch(`${this.apiBaseUrl}${path}`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: body ? JSON.stringify(body) : undefined,
        });

        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
            throw new Error(data.message || `Request to ${path} failed`);
        }

        return data.data ?? data;
    }

    _setStatus(status) {
        this.status = status;
        this.dispatchEvent(new CustomEvent("status", { detail: { status } }));
    }

    _error(message, cause) {
        this.dispatchEvent(
            new CustomEvent("error", { detail: { message, cause } }),
        );
    }

    static _floatTo16BitPCM(float32Array) {
        const out = new Int16Array(float32Array.length);
        for (let i = 0; i < float32Array.length; i++) {
            const s = Math.max(-1, Math.min(1, float32Array[i]));
            out[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
        }
        return out;
    }

    static _arrayBufferToBase64(buffer) {
        let binary = "";
        const bytes = new Uint8Array(buffer);
        for (let i = 0; i < bytes.byteLength; i++) {
            binary += String.fromCharCode(bytes[i]);
        }
        return window.btoa(binary);
    }

    static _base64ToArrayBuffer(base64) {
        const binary = window.atob(base64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) {
            bytes[i] = binary.charCodeAt(i);
        }
        return bytes.buffer;
    }
}
